import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoals } from '../hooks/useGoals';
import type { Goal, GoalDuration, GoalStats } from '../types/goals';
import { useLocation } from 'react-router-dom';
import GoalsErrorState from '../components/ui/GoalsErrorState';
import GoalsFilters from '../components/ui/GoalsFilters';
import GoalsHeader from '../components/ui/GoalsHeader';
import GoalsLoadingState from '../components/ui/GoalsLoadingState';
import GoalsModals from '../components/ui/GoalsModals';
import GoalsPagination from '../components/ui/GoalsPagination';
import GoalsStats from '../components/ui/GoalsStats';
import GoalsGrid from '../components/ui/GoalsGrid';
import { useDailyTasks } from '../hooks/useTask';

export interface PaginationData {
  page: number;
  totalPages: number;
  limit: number;
  total: number;
}

export interface GoalFilters {
  page: number;
  limit: number;
  completed?: boolean;
  duration?: GoalDuration;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
}

export interface GoalsHeaderProps {
  onNewGoal: () => void;
  stats: GoalStats;
}

const defaultPagination = {
  page: 1,
  totalPages: 1,
  limit: 10,
  total: 0
};

const defaultStats: GoalStats = {
  totalGoals: 0,
  completedGoals: 0,
  completionRate: 0,
  completedTasks: 0,
  onTrackGoals: 0
};

export default function GoalsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { toggleBottomNav, hideBottomNav } = useOutletContext();

  // Estado para paginación y filtros
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    completed: undefined as boolean | undefined,
    duration: undefined as GoalDuration | undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });

  const {
    goals,
    milestones,
    dailyTasks,
    pagination = defaultPagination,
    fetchGoals,
    fetchGoalDetails,
    isLoading,
    error,
    setState,
    generalStats
  } = useGoals(filters);

  const {
    toggleTaskCompletion,
  } = useDailyTasks()

  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    fetchGoals(filters);
    if (location.state?.openNewGoal) {
      setShowWizard(true);
      window.history.replaceState({}, document.title);
    }
  }, [filters]);

  const handleGoalClick = useCallback(async (goal: Goal) => {
    if (!hideBottomNav) {
      toggleBottomNav();
    }
    await fetchGoalDetails(goal._id);
    setSelectedGoal(goal);
  }, [fetchGoalDetails, hideBottomNav, toggleBottomNav]);

  const handleOnCloseDetails = useCallback(() => {
    if (hideBottomNav) {
      toggleBottomNav();
    }
    setSelectedGoal(null);
  }, [hideBottomNav, toggleBottomNav]);

  const handleNewGoal = useCallback(() => {
    if (!hideBottomNav) {
      toggleBottomNav();
    }

    setShowWizard(true);
  }, [hideBottomNav, toggleBottomNav]);

  const handleOnCloseWizard = useCallback(() => {
    if (hideBottomNav) {
      toggleBottomNav();
    }
    setShowWizard(false);
  }, [hideBottomNav, toggleBottomNav]);

  // Función para actualizar la lista de metas después de crear una nueva
  const handleGoalCreated = useCallback(() => {
    // Actualizamos la lista de metas
    fetchGoals({
      ...filters,
      // Volvemos a la primera página para mostrar las metas más recientes
      page: 1
    });

    // Actualizamos el filtro a la primera página
    setFilters(prev => ({ ...prev, page: 1 }));

  }, [fetchGoals, filters]);

  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const handleIsEmpty = useMemo(() => !goals.length && !isLoading, [goals, isLoading]);
  const handleGoalsExist = useMemo(() => goals.length > 0, [goals]);

  const handleToggleTaskCompletion = async (taskId: string) => {
    setState(prev => ({
      ...prev,
      dailyTasks: prev.dailyTasks.map(task => {
        if (task._id === taskId) {
          return { ...task, isCompleted: !task.isCompleted };
        }
        return task;
      }
      )
    }));

    try {
      toggleTaskCompletion(taskId);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="space-y-6 mb-20">
      <GoalsHeader
        onNewGoal={handleNewGoal}
        stats={generalStats || defaultStats}
      />

      {/* Stats Dashboard - New */}
      <GoalsStats stats={generalStats} />

      {/* Filters - New */}
      <GoalsFilters
        filters={filters}
        onChange={setFilters}
      />

      {/* Error and Loading States */}
      <GoalsErrorState error={error} />
      <GoalsLoadingState isLoading={isLoading} goalsExist={handleGoalsExist} t={t} />

      {/* Goals Grid */}
      <GoalsGrid
        goals={goals}
        onGoalClick={handleGoalClick}
        isEmpty={handleIsEmpty}
        t={t}
      />

      {/* Pagination - New */}
      <GoalsPagination
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      {/* Modals */}
      <GoalsModals
        selectedGoal={selectedGoal}
        showWizard={showWizard}
        onCloseDetails={handleOnCloseDetails}
        onCloseWizard={handleOnCloseWizard}
        onGoalCreated={handleGoalCreated}
        milestones={milestones}
        dailyTasks={dailyTasks}
        toggleTaskCompletion={handleToggleTaskCompletion}
      />
    </div>
  );
}